import { useCallback, useContext, useRef, useState } from 'react';
import { AdyenComponentContainer } from '@/components/expressPayment/AdyenComponentContainer';
import { useAdyenGooglePay } from './useAdyenGooglePay';
import type { ExpressCheckoutComponentProps } from '@/utils/expressPayment';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { GooglePayButtonPlaceholder } from '@/components/Wallets/WalletButton/GooglePayButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { EXPRESS_WALLET_NAME, WALLET_AVAILABILITY_REASON } from '@/utils/adyenWalletAvailability';

export const AdyenGooglePay = ({ onReady }: ExpressCheckoutComponentProps) => {
  const context = useContext(CheckoutContext);
  const reportWalletAvailability = useExpressWalletFallback();
  const [state, setState] = useState<WalletReadyState>('loading');
  const mountTimeRef = useRef(Date.now());

  const onClick = useCallback(
    (resolve: () => void) => {
      trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });
      resolve();
    },
    [context]
  );

  const handleReady = useCallback(
    (isAvailable: boolean, reason?: string) => {
      reportWalletAvailability(isAvailable, {
        wallet: EXPRESS_WALLET_NAME.GOOGLE_PAY,
        provider: ProviderEnum.ADYEN,
        reason: isAvailable ? undefined : (reason ?? WALLET_AVAILABILITY_REASON.UNKNOWN),
        loadTimeMs: Date.now() - mountTimeRef.current,
      });
      setState(isAvailable ? 'ready' : 'unavailable');
      onReady?.(isAvailable, reason);
    },
    [onReady, reportWalletAvailability]
  );

  const { googlePayElement } = useAdyenGooglePay({ onReady: handleReady, onClick });

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported isExpress />;
  }

  return (
    <WalletButtonShell
      state={state}
      placeholder={<GooglePayButtonPlaceholder />}
      testId="wallet-btn-google"
      usePlaceholderAsVisual
    >
      <AdyenComponentContainer element={googlePayElement} />
    </WalletButtonShell>
  );
};
